/**
 * Grid utilities for Nexus customizable grid system
 * Handles grid calculations, collision detection, and tile positioning
 */

// Responsive tile adaptation levels
export interface TileAdaptation {
  level: 'large' | 'medium' | 'small' | 'tiny';
  padding: number;
  showDescription: boolean;
  descriptionOpacity: number;
  maxTitleLines: number;
  maxDescriptionLines: number;
}

/**
 * Calculate responsive tile properties based on available space
 */
export function calculateTileAdaptation(
  tileWidth: number, 
  tileHeight: number
): TileAdaptation {
  // Define thresholds (erring on the side of less padding)
  const isLarge = tileWidth > 200 && tileHeight > 150;
  const isMedium = tileWidth > 120 && tileHeight > 100;
  const isSmall = tileWidth > 80 && tileHeight > 70;
  
  if (isLarge) {
    return {
      level: 'large',
      padding: 16,
      showDescription: true,
      descriptionOpacity: 1,
      maxTitleLines: 3,
      maxDescriptionLines: 2
    };
  } else if (isMedium) {
    return {
      level: 'medium', 
      padding: 8,
      showDescription: true,
      descriptionOpacity: 1,
      maxTitleLines: 2,
      maxDescriptionLines: 1
    };
  } else if (isSmall) {
    // Gradual fade: opacity based on how close we are to tiny threshold
    const fadeProgress = Math.max(0, (tileWidth - 80) / 40); // 0 at 80px, 1 at 120px
    const heightFadeProgress = Math.max(0, (tileHeight - 70) / 30); // 0 at 70px, 1 at 100px
    const combinedFade = Math.min(fadeProgress, heightFadeProgress);
    
    return {
      level: 'small',
      padding: 4,
      showDescription: true,
      descriptionOpacity: combinedFade * 0.8, // Fade from 0.8 to 0
      maxTitleLines: 2,
      maxDescriptionLines: 1
    };
  } else {
    // Tiny tiles - minimal content
    return {
      level: 'tiny',
      padding: 2,
      showDescription: false,
      descriptionOpacity: 0,
      maxTitleLines: 1,
      maxDescriptionLines: 0
    };
  }
}

export interface GridPosition {
  row: number;
  column: number;
}

export interface GridSize {
  width: number; // column span
  height: number; // row span
}

// NEW ARCHITECTURE: Separate Headers from Categories

/**
 * GridHeader: Always present infrastructure for each row/column
 * These are the clickable slots that exist for every row/column
 */
export interface GridHeader {
  id: string;
  index: number; // 1-based index (row or column) - always 1:1 with grid rows/columns
  type: 'row' | 'column';
  title?: string;
  startIndex?: number;
  endIndex?: number;
}

/**
 * GridCategory: Optional content that spans within header spaces
 * These are the visual groupings that can span multiple rows/columns
 */
export interface GridCategory {
  id: string;
  title: string;
  startIndex: number; // 1-based index (row or column)
  endIndex: number; // 1-based index (row or column) - inclusive
  type: 'row' | 'column';
  restrictDelete?: boolean; // Prevents accidental deletion of the category - Added to fix linting errors
  style?: {
    backgroundColor?: string;
    textColor?: string; // Font color (renamed from 'color' for clarity)
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    alignItems?: 'flex-start' | 'center' | 'flex-end'; // Vertical alignment
    fontSize?: string;
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    textDecoration?: 'none' | 'underline';
    gradientColor?: string;
    gradientDirection?: string;
    borderColor?: string;
    borderThickness?: string;
    outlineColor?: string;
    outlineThickness?: string;
    applyBorderToBackground?: boolean;
  };
}

// LEGACY: Keep old GridHeader interface for migration compatibility
export interface LegacyGridHeader {
  id: string;
  title: string;
  startIndex: number; // 1-based index (row or column)
  endIndex: number; // 1-based index (row or column) - inclusive
  style?: {
    backgroundColor?: string;
    textColor?: string; // Font color (renamed from 'color' for clarity)
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    alignItems?: 'flex-start' | 'center' | 'flex-end'; // Vertical alignment
    fontSize?: string;
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    textDecoration?: 'none' | 'underline';
    gradientColor?: string;
    gradientDirection?: string;
    borderColor?: string;
    borderThickness?: string;
  };
}

export interface GridLayout {
  rows: number;
  columns: number;
  gap: number;
  scrollRowThreshold?: number; // Smart sizing threshold
  scrollColumnThreshold?: number; // Smart sizing threshold
  showRowHeaders?: boolean;
  showColumnHeaders?: boolean;
  backgroundColor?: string; // Grid background color
  // NEW ARCHITECTURE: Separate headers (infrastructure) from categories (content)
  rowHeaders?: GridHeader[]; // Always present 1:1 with rows
  columnHeaders?: GridHeader[]; // Always present 1:1 with columns
  rowCategories?: GridCategory[]; // Optional spanning content
  columnCategories?: GridCategory[]; // Optional spanning content
  // LEGACY: For migration compatibility
  legacyRowHeaders?: LegacyGridHeader[];
  legacyColumnHeaders?: LegacyGridHeader[];
}

export interface GridTile {
  id: string;
  pageId?: string;
  position: GridPosition;
  size: GridSize;
  type: string;
  title: string;
  description?: string;
  tag?: string; // Tile tag icon (⚠️, 🟩, 🔴, ❔, or null)
  config: any;
  linkedPageId?: string;
  userAccess?: 'FULL' | 'EDIT' | 'CONTRIBUTE' | 'READ' | 'VIEW' | 'RESTRICTED';
}

export type GridMode = 'read' | 'edit';

export interface GridCell {
  row: number;
  column: number;
  isEmpty: boolean;
  tileId?: string;
}

/**
 * Creates a grid matrix representation for collision detection
 */
export function createGridMatrix(layout: GridLayout, tiles: GridTile[]): GridCell[][] {
  const matrix: GridCell[][] = [];
  
  // Initialize empty grid
  for (let row = 1; row <= layout.rows; row++) {
    matrix[row - 1] = [];
    for (let col = 1; col <= layout.columns; col++) {
      matrix[row - 1][col - 1] = {
        row,
        column: col,
        isEmpty: true
      };
    }
  }
  
  // Mark occupied cells
  tiles.forEach(tile => {
    const { position, size } = tile;
    for (let row = position.row; row < position.row + size.height; row++) {
      for (let col = position.column; col < position.column + size.width; col++) {
        if (row >= 1 && row <= layout.rows && col >= 1 && col <= layout.columns) {
          matrix[row - 1][col - 1] = {
            row,
            column: col,
            isEmpty: false,
            tileId: tile.id
          };
        }
      }
    }
  });
  
  return matrix;
}

/**
 * Checks if a tile can be placed at a specific position without collision
 */
export function canPlaceTile(
  layout: GridLayout,
  tiles: GridTile[],
  position: GridPosition,
  size: GridSize,
  excludeTileId?: string
): boolean {
  // Check bounds
  if (position.row < 1 || position.column < 1) {
    return false;
  }
  if (position.row + size.height - 1 > layout.rows) {
    return false;
  }
  if (position.column + size.width - 1 > layout.columns) {
    return false;
  }
  
  const tilesToCheck = tiles.filter(t => t.id !== excludeTileId);
  
  const matrix = createGridMatrix(layout, tilesToCheck);
  
  // Check for collisions
  for (let row = position.row; row < position.row + size.height; row++) {
    for (let col = position.column; col < position.column + size.width; col++) {
      const cell = matrix[row - 1][col - 1];
      if (!cell.isEmpty) {
        return false;
      }
    }
  }
  
  return true;
}

/**
 * Finds the next available position for a tile of given size
 */
export function findNextAvailablePosition(
  layout: GridLayout,
  tiles: GridTile[],
  size: GridSize
): GridPosition | null {
  for (let row = 1; row <= layout.rows - size.height + 1; row++) {
    for (let col = 1; col <= layout.columns - size.width + 1; col++) {
      const position = { row, column: col };
      if (canPlaceTile(layout, tiles, position, size)) {
        return position;
      }
    }
  }
  return null;
}

/**
 * Finds the next available position with smart placement logic for tile moves
 * Searches row by row, column by column, wrapping around but avoiding original position
 * If no space found, returns position that would require grid expansion
 */
export function findSmartPlacementPosition(
  layout: GridLayout,
  tiles: GridTile[],
  size: GridSize,
  preferredPosition?: GridPosition,
  originalPosition?: GridPosition
): { position: GridPosition; needsExpansion: boolean } {
  
  // First, try the preferred position if provided
  if (preferredPosition && canPlaceTile(layout, tiles, preferredPosition, size)) {
    return { position: preferredPosition, needsExpansion: false };
  }

  // Generate all possible positions in search order (row by row, column by column)
  const searchPositions: GridPosition[] = [];
  
  for (let row = 1; row <= layout.rows - size.height + 1; row++) {
    for (let col = 1; col <= layout.columns - size.width + 1; col++) {
      const position = { row, column: col };
      
      // Skip the original position to avoid placing back where it came from
      if (originalPosition && 
          position.row === originalPosition.row && 
          position.column === originalPosition.column) {
        continue;
      }
      
      searchPositions.push(position);
    }
  }

  // Find first available position
  for (const position of searchPositions) {
    if (canPlaceTile(layout, tiles, position, size)) {
      return { position, needsExpansion: false };
    }
  }

  // No available space - need to expand grid
  // Try adding a column first (expand horizontally)
  const expandedLayout = {
    ...layout,
    columns: layout.columns + 1
  };

  // Try positions in the new column
  for (let row = 1; row <= expandedLayout.rows - size.height + 1; row++) {
    const position = { row, column: expandedLayout.columns - size.width + 1 };
    if (canPlaceTile(expandedLayout, tiles, position, size)) {
      return { position, needsExpansion: true };
    }
  }

  // If even the new column doesn't work, place at top of new column
  return { 
    position: { row: 1, column: layout.columns + 1 }, 
    needsExpansion: true 
  };
}

/**
 * Comprehensive collision protection with systematic placement algorithm
 * Implements the exact placement order logic: right → next row column 1 → wrap to (1,1)
 * If grid is full, expands by adding a column and places tile there
 */
export function findCollisionFreePosition(
  layout: GridLayout,
  tiles: GridTile[],
  targetPosition: GridPosition,
  size: GridSize,
  excludeTileId?: string
): { position: GridPosition; needsExpansion: boolean; expandedLayout?: GridLayout } {
  
  // Step 1: Boundary validation - adjust target position to fit within grid bounds
  let adjustedX = targetPosition.column;
  let adjustedY = targetPosition.row;
  
  // If target column exceeds grid width, snap to rightmost valid column
  if (adjustedX + size.width - 1 > layout.columns) {
    adjustedX = Math.max(1, layout.columns - size.width + 1);
  }
  
  // If target row exceeds grid height, snap to bottom valid row
  if (adjustedY + size.height - 1 > layout.rows) {
    const oldY = adjustedY;
    adjustedY = Math.max(1, layout.rows - size.height + 1);

  }
  
  const startPosition = { row: adjustedY, column: adjustedX };
  
  // Step 2: Create list of tiles excluding the one we're placing (for moves)
  const otherTiles = excludeTileId ? tiles.filter(t => t.id !== excludeTileId) : tiles;
  
  // Step 3: Systematic search following the specified placement order
  const searchOrder: GridPosition[] = [];
  
  // Generate search positions starting from adjusted position
  let currentRow = startPosition.row;
  let currentCol = startPosition.column;
  const visitedPositions = new Set<string>();
  const maxPositions = (layout.rows - size.height + 1) * (layout.columns - size.width + 1);
  
  for (let i = 0; i < maxPositions; i++) {
    const posKey = `${currentRow}-${currentCol}`;
    
    if (!visitedPositions.has(posKey)) {
      visitedPositions.add(posKey);
      searchOrder.push({ row: currentRow, column: currentCol });
    }
    
    // Apply placement order logic:
    // 1. Move one column to the right
    if (currentCol + size.width <= layout.columns) {
      currentCol++;
    } else {
      // 2. If no columns to the right, move to column 1 of next row down
      if (currentRow + size.height <= layout.rows) {
        currentRow++;
        currentCol = 1;
      } else {
        // 3. If no rows below, wrap to column 1, row 1
        currentRow = 1;
        currentCol = 1;
      }
    }
  }
  
  // Step 4: Try each position in search order
  for (let i = 0; i < searchOrder.length; i++) {
    const position = searchOrder[i];
    const canPlace = canPlaceTile(layout, otherTiles, position, size);
    if (canPlace) {
      return { position, needsExpansion: false };
    }
  }
  
  // Step 5: Grid is full - expand by adding a column and place in new column
  const expandedLayout: GridLayout = {
    ...layout,
    columns: layout.columns + 1
  };
  
  const newColumnPosition = { 
    row: startPosition.row, 
    column: layout.columns + 1 
  };
  
  // Ensure the tile fits in the new column (adjust row if needed)
  let finalRow = newColumnPosition.row;
  if (finalRow + size.height - 1 > layout.rows) {
    finalRow = Math.max(1, layout.rows - size.height + 1);
  }
  
  const finalPosition = { row: finalRow, column: newColumnPosition.column };
  
  return {
    position: finalPosition,
    needsExpansion: true,
    expandedLayout
  };
}

/**
 * Utility function for future tile restoration functionality
 * Finds a safe position for restoring a deleted tile with collision protection
 */
export function findRestorationPosition(
  layout: GridLayout,
  tiles: GridTile[],
  originalPosition: GridPosition,
  size: GridSize
): { position: GridPosition; needsExpansion: boolean; expandedLayout?: GridLayout } {
  return findCollisionFreePosition(layout, tiles, originalPosition, size);
}

/**
 * Gets all empty cells in the grid
 */
export function getEmptyCells(layout: GridLayout, tiles: GridTile[]): GridPosition[] {
  const matrix = createGridMatrix(layout, tiles);
  const emptyCells: GridPosition[] = [];
  
  for (let row = 0; row < layout.rows; row++) {
    for (let col = 0; col < layout.columns; col++) {
      if (matrix[row][col].isEmpty) {
        emptyCells.push({ row: row + 1, column: col + 1 });
      }
    }
  }
  
  return emptyCells;
}

/**
 * Calculates the maximum possible size for a tile at a given position
 */
export function getMaxTileSize(
  layout: GridLayout,
  tiles: GridTile[],
  position: GridPosition,
  excludeTileId?: string
): GridSize {
  const matrix = createGridMatrix(layout, tiles.filter(t => t.id !== excludeTileId));
  
  let maxWidth = 1;
  let maxHeight = 1;
  
  // Find maximum width
  for (let col = position.column; col <= layout.columns; col++) {
    if (matrix[position.row - 1][col - 1].isEmpty) {
      maxWidth = col - position.column + 1;
    } else {
      break;
    }
  }
  
  // Find maximum height
  for (let row = position.row; row <= layout.rows; row++) {
    if (matrix[row - 1][position.column - 1].isEmpty) {
      maxHeight = row - position.row + 1;
    } else {
      break;
    }
  }
  
  // Check if the rectangle is valid
  for (let row = position.row; row < position.row + maxHeight; row++) {
    for (let col = position.column; col < position.column + maxWidth; col++) {
      if (!matrix[row - 1][col - 1].isEmpty) {
        // Reduce size to fit
        maxWidth = Math.min(maxWidth, col - position.column);
        maxHeight = Math.min(maxHeight, row - position.row);
        break;
      }
    }
  }
  
  return { width: Math.max(1, maxWidth), height: Math.max(1, maxHeight) };
}

/**
 * Validates if adding rows/columns would break existing tiles
 */
export function canAddRows(layout: GridLayout, count: number): boolean {
  return layout.rows + count <= 20; // Maximum reasonable grid size
}

export function canAddColumns(layout: GridLayout, count: number): boolean {
  return layout.columns + count <= 20; // Maximum reasonable grid size
}

export function canRemoveRows(layout: GridLayout, tiles: GridTile[], count: number): boolean {
  if (layout.rows - count < 1) return false;
  
  const newRowCount = layout.rows - count;
  return !tiles.some(tile => tile.position.row + tile.size.height - 1 > newRowCount);
}

export function canRemoveColumns(layout: GridLayout, tiles: GridTile[], count: number): boolean {
  if (layout.columns - count < 1) return false;
  
  const newColumnCount = layout.columns - count;
  return !tiles.some(tile => tile.position.column + tile.size.width - 1 > newColumnCount);
}

/**
 * Smart deletion: Check if a specific row can be removed with span reduction
 * Returns true if removal is possible (either no tiles affected or tiles can have span reduced)
 */
export function canRemoveRowAt(layout: GridLayout, tiles: GridTile[], rowIndex: number): boolean {
  if (layout.rows <= 1) return false; // Can't remove last row
  if (rowIndex < 1 || rowIndex > layout.rows) return false; // Invalid row index
  
  // Check each tile to see if removal would completely delete it
  for (const tile of tiles) {
    const tileStartRow = tile.position.row;
    const tileEndRow = tile.position.row + tile.size.height - 1;
    
    // If tile only occupies the row being removed, deletion would delete the tile entirely
    if (tileStartRow === rowIndex && tileEndRow === rowIndex) {
      return false; // Block deletion - would completely remove this tile
    }
    
    // For tiles that span across the removed row, we can reduce their span
    // For tiles above or below the removed row, they'll just shift position
    // Both scenarios are handled gracefully, so continue checking
  }
  
  return true; // Safe to remove
}

/**
 * Smart deletion: Check if a specific column can be removed with span reduction
 * Returns true if removal is possible (either no tiles affected or tiles can have span reduced)
 */
export function canRemoveColumnAt(layout: GridLayout, tiles: GridTile[], columnIndex: number): boolean {
  if (layout.columns <= 1) return false; // Can't remove last column
  if (columnIndex < 1 || columnIndex > layout.columns) return false; // Invalid column index
  
  // Check each tile to see if removal would completely delete it
  for (const tile of tiles) {
    const tileStartColumn = tile.position.column;
    const tileEndColumn = tile.position.column + tile.size.width - 1;
    
    // If tile only occupies the column being removed, deletion would delete the tile entirely
    if (tileStartColumn === columnIndex && tileEndColumn === columnIndex) {
      return false; // Block deletion - would completely remove this tile
    }
    
    // For tiles that span across the removed column, we can reduce their span
    // For tiles to the left or right of the removed column, they'll just shift position
    // Both scenarios are handled gracefully, so continue checking
  }
  
  return true; // Safe to remove
}

/**
 * Converts grid position to CSS grid coordinates
 */
export function positionToCSS(position: GridPosition, size: GridSize) {
  return {
    gridColumn: `${position.column} / span ${size.width}`,
    gridRow: `${position.row} / span ${size.height}`
  };
}

/**
 * Calculates pixel coordinates from grid position for drag operations
 */
export function gridToPixels(
  position: GridPosition,
  cellSize: { width: number; height: number },
  gap: number
): { x: number; y: number } {
  return {
    x: (position.column - 1) * (cellSize.width + gap),
    y: (position.row - 1) * (cellSize.height + gap)
  };
}

/**
 * Converts pixel coordinates back to grid position
 */
export function pixelsToGrid(
  pixels: { x: number; y: number },
  cellSize: { width: number; height: number },
  gap: number
): GridPosition {
  return {
    column: Math.max(1, Math.round(pixels.x / (cellSize.width + gap)) + 1),
    row: Math.max(1, Math.round(pixels.y / (cellSize.height + gap)) + 1)
  };
}

/**
 * Snaps a position to the nearest valid grid cell
 */
export function snapToGrid(
  layout: GridLayout,
  tiles: GridTile[],
  position: GridPosition,
  size: GridSize,
  excludeTileId?: string
): GridPosition {
  // Try exact position first
  if (canPlaceTile(layout, tiles, position, size, excludeTileId)) {
    return position;
  }
  
  // Search for nearby valid positions
  const searchRadius = 3;
  for (let radius = 1; radius <= searchRadius; radius++) {
    for (let rowOffset = -radius; rowOffset <= radius; rowOffset++) {
      for (let colOffset = -radius; colOffset <= radius; colOffset++) {
        if (Math.abs(rowOffset) + Math.abs(colOffset) === radius) {
          const testPosition = {
            row: Math.max(1, position.row + rowOffset),
            column: Math.max(1, position.column + colOffset)
          };
          
          if (canPlaceTile(layout, tiles, testPosition, size, excludeTileId)) {
            return testPosition;
          }
        }
      }
    }
  }
  
  // Fallback to original position if no valid position found
  return position;
}

/**
 * Gets tiles that would be affected by a layout change
 */
export function getAffectedTiles(
  tiles: GridTile[],
  operation: 'addRow' | 'removeRow' | 'addColumn' | 'removeColumn',
  index: number
): GridTile[] {
  switch (operation) {
    case 'removeRow':
      return tiles.filter(tile => 
        tile.position.row + tile.size.height - 1 >= index
      );
    case 'removeColumn':
      return tiles.filter(tile => 
        tile.position.column + tile.size.width - 1 >= index
      );
    default:
      return [];
  }
}

/**
 * Creates default headers for all rows/columns when first enabling headers
 * These are structural containers only - styling is handled by GridCategory objects
 */
export function createDefaultHeaders(
  type: 'row' | 'column',
  count: number
): GridHeader[] {
  const headers: GridHeader[] = [];
  
  for (let i = 1; i <= count; i++) {
    headers.push({
      id: `${type}-header-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      index: i, // 1-based index as per GridHeader interface
      type
    });
  }
  
  return headers;
}

/**
 * Ensures all headers have unique IDs by regenerating duplicate ones
 */
export function ensureUniqueHeaderIds(headers: GridHeader[]): GridHeader[] {
  const seenIds = new Set<string>();
  const uniqueHeaders: GridHeader[] = [];
  
  for (const header of headers) {
    let uniqueId = header.id;
    
    // If we've seen this ID before, generate a new unique one
    if (seenIds.has(uniqueId)) {
      const oldId = uniqueId;
      uniqueId = `header-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log(`ensureUniqueHeaderIds: Duplicate ID detected! Changed ${oldId} to ${uniqueId}`);
    }
    
    seenIds.add(uniqueId);
    uniqueHeaders.push({
      ...header,
      id: uniqueId
    });
  }
  
  return uniqueHeaders;
}

/**
 * Checks if two category ranges overlap
 */
export function categoriesOverlap(category1: GridCategory, category2: GridCategory): boolean {
  return !(category1.endIndex < category2.startIndex || category2.endIndex < category1.startIndex);
}

/**
 * Validates that categories don't overlap and cover the expected range
 */
export function validateCategories(categories: GridCategory[], expectedCount: number): boolean {
  if (!categories || categories.length === 0) return false;
  
  // Sort categories by start index
  const sortedCategories = [...categories].sort((a, b) => a.startIndex - b.startIndex);
  
  // Check for overlaps
  for (let i = 0; i < sortedCategories.length - 1; i++) {
    if (categoriesOverlap(sortedCategories[i], sortedCategories[i + 1])) {
      return false;
    }
  }
  
  // Check that all indices from 1 to expectedCount are covered
  const coveredIndices = new Set<number>();
  sortedCategories.forEach(category => {
    for (let i = category.startIndex; i <= category.endIndex; i++) {
      coveredIndices.add(i);
    }
  });
  
  for (let i = 1; i <= expectedCount; i++) {
    if (!coveredIndices.has(i)) {
      return false;
    }
  }
  
  return true;
}

/**
 * Finds the header at a specific index
 */
export function findHeaderForIndex(headers: GridHeader[], index: number): GridHeader | null {
  return headers.find(header => header.index === index) || null;
}

/**
 * Calculates the total margin space needed for headers
 */
export function calculateHeaderSpace(showHeaders: boolean): number {
  return showHeaders ? 40 : 0; // 40px for header height/width
}

/**
 * Gets the margin spacing between tiles and category boundaries
 */
export function getCategoryMargin(): number {
  return 8; // 8px margin between tiles and category boundaries
}

/**
 * Calculates margins for a tile based on its position and header categories
 */
export function calculateTileMargins(
  _: GridTile,
  layout: GridLayout
): { marginTop: number; marginRight: number; marginBottom: number; marginLeft: number } {
  const margins = { marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 };
  
  // Simple uniform margins when headers are active
  // Apply 5px top/bottom margins when row headers are active
  if (layout.showRowHeaders && layout.rowHeaders && layout.rowHeaders.length > 0) {
    margins.marginTop = 5;
    margins.marginBottom = 5;
  }
  
  // Apply 5px left/right margins when column headers are active  
  if (layout.showColumnHeaders && layout.columnHeaders && layout.columnHeaders.length > 0) {
    margins.marginLeft = 5;
    margins.marginRight = 5;
  }

  return margins;
}

/**
 * Adjusts tiles when a new row is inserted at a specific position
 */
export function adjustTilesForRowInsertion(
  tiles: GridTile[],
  insertionRow: number
): GridTile[] {
  return tiles.map(tile => {
    const tileStartRow = tile.position.row;
    const tileEndRow = tile.position.row + tile.size.height - 1;

    // Tiles that span across the insertion point: increase row span
    if (tileStartRow < insertionRow && tileEndRow >= insertionRow) {
      return {
        ...tile,
        size: {
          ...tile.size,
          height: tile.size.height + 1
        }
      };
    }

    // Tiles entirely below the insertion point: shift down by 1
    if (tileStartRow >= insertionRow) {
      return {
        ...tile,
        position: {
          ...tile.position,
          row: tile.position.row + 1
        }
      };
    }

    // Tiles entirely above the insertion point: no change
    return tile;
  });
}

/**
 * Adjusts tiles when a new column is inserted at a specific position
 */
export function adjustTilesForColumnInsertion(
  tiles: GridTile[],
  insertionColumn: number
): GridTile[] {
  return tiles.map(tile => {
    const tileStartCol = tile.position.column;
    const tileEndCol = tile.position.column + tile.size.width - 1;

    // Tiles that span across the insertion point: increase column span
    if (tileStartCol < insertionColumn && tileEndCol >= insertionColumn) {
      return {
        ...tile,
        size: {
          ...tile.size,
          width: tile.size.width + 1
        }
      };
    }

    // Tiles entirely to the right of the insertion point: shift right by 1
    if (tileStartCol >= insertionColumn) {
      return {
        ...tile,
        position: {
          ...tile.position,
          column: tile.position.column + 1
        }
      };
    }

    // Tiles entirely to the left of the insertion point: no change
    return tile;
  });
}

/**
 * Adjusts headers when a new row is inserted at a specific position
 */
export function adjustHeadersForRowInsertion(
  headers: GridHeader[],
  insertionRow: number
): GridHeader[] {
  return headers.map(header => {
    // Headers at or after the insertion point: shift index down
    if (header.index >= insertionRow) {
      return {
        ...header,
        index: header.index + 1
      };
    }
    return header;
  });
}

/**
 * Adjusts headers when a new column is inserted at a specific position
 */
export function adjustHeadersForColumnInsertion(
  headers: GridHeader[],
  insertionColumn: number
): GridHeader[] {
  return headers.map(header => {
    // Headers at or after the insertion point: shift index right
    if (header.index >= insertionColumn) {
      return {
        ...header,
        index: header.index + 1
      };
    }
    return header;
  });
}

/**
 * Resolves tile collisions after row/column insertion by pushing overlapping tiles
 */
export function resolveCollisionsAfterInsertion(
  layout: GridLayout,
  tiles: GridTile[]
): GridTile[] {
  const resolvedTiles: GridTile[] = [];

  // Sort tiles by position (top-left first) to process in order
  const sortedTiles = [...tiles].sort((a, b) => {
    if (a.position.row !== b.position.row) {
      return a.position.row - b.position.row;
    }
    return a.position.column - b.position.column;
  });

  for (const tile of sortedTiles) {
    let newPosition = { ...tile.position };
    
    // Find the next available position if current position is occupied
    while (!canPlaceTile(layout, resolvedTiles, newPosition, tile.size, tile.id)) {
      // Try moving down first
      if (newPosition.row + tile.size.height <= layout.rows) {
        newPosition.row += 1;
      } else {
        // If can't move down, try moving right and reset row
        newPosition.column += 1;
        newPosition.row = 1;
        
        // If we've gone beyond the grid, place at current position
        if (newPosition.column + tile.size.width > layout.columns) {
          break;
        }
      }
    }

    resolvedTiles.push({
      ...tile,
      position: newPosition
    });
  }

  return resolvedTiles;
}

/**
 * Adjusts headers when a row is removed
 */
export function adjustHeadersForRowRemoval(
  headers: GridHeader[],
  removedRow: number
): GridHeader[] {
  return headers
    .filter(header => header.index !== removedRow) // Remove header at the removed row
    .map(header => {
      // Headers after the removed row: shift index up
      if (header.index > removedRow) {
        return {
          ...header,
          index: header.index - 1
        };
      }
      return header;
    });
}

/**
 * Adjusts headers when a column is removed
 */
export function adjustHeadersForColumnRemoval(
  headers: GridHeader[],
  removedColumn: number
): GridHeader[] {
  return headers
    .filter(header => header.index !== removedColumn) // Remove header at the removed column
    .map(header => {
      // Headers after the removed column: shift index left
      if (header.index > removedColumn) {
        return {
          ...header,
          index: header.index - 1
        };
      }
      return header;
    });
}

/**
 * Adjusts tiles when a row is removed
 */
export function adjustTilesForRowRemoval(tiles: GridTile[], removedRow: number): GridTile[] {
  return tiles
    .map(tile => {
      const tileStartRow = tile.position.row;
      const tileEndRow = tile.position.row + tile.size.height - 1;

      // Tiles entirely above the removed row: no change
      if (tileEndRow < removedRow) {
        return tile;
      }

      // Tiles entirely below the removed row: shift up by 1
      if (tileStartRow > removedRow) {
        return {
          ...tile,
          position: {
            ...tile.position,
            row: tile.position.row - 1
          }
        };
      }

      // Tiles that span across the removed row: decrease height by 1
      if (tileStartRow <= removedRow && tileEndRow >= removedRow) {
        const newHeight = Math.max(1, tile.size.height - 1);
        return {
          ...tile,
          size: {
            ...tile.size,
            height: newHeight
          }
        };
      }

      return tile;
    })
    .filter(tile => {
      // Remove tiles that would have zero height or invalid position
      return tile.size.height > 0 && tile.position.row > 0;
    });
}

/**
 * Adjusts tiles when a column is removed
 */
export function adjustTilesForColumnRemoval(tiles: GridTile[], removedColumn: number): GridTile[] {
  return tiles
    .map(tile => {
      const tileStartColumn = tile.position.column;
      const tileEndColumn = tile.position.column + tile.size.width - 1;

      // Tiles entirely to the left of the removed column: no change
      if (tileEndColumn < removedColumn) {
        return tile;
      }

      // Tiles entirely to the right of the removed column: shift left by 1
      if (tileStartColumn > removedColumn) {
        return {
          ...tile,
          position: {
            ...tile.position,
            column: tile.position.column - 1
          }
        };
      }

      // Tiles that span across the removed column: decrease width by 1
      if (tileStartColumn <= removedColumn && tileEndColumn >= removedColumn) {
        const newWidth = Math.max(1, tile.size.width - 1);
        return {
          ...tile,
          size: {
            ...tile.size,
            width: newWidth
          }
        };
      }

      return tile;
    })
    .filter(tile => {
      // Remove tiles that would have zero width or invalid position
      return tile.size.width > 0 && tile.position.column > 0;
    });
}

// NEW ARCHITECTURE: Header/Category utility functions

/**
 * Generate headers for all rows or columns (always 1:1 with grid dimensions)
 */
export function generateHeaders(type: 'row' | 'column', count: number): GridHeader[] {
  const headers: GridHeader[] = [];
  for (let i = 1; i <= count; i++) {
    headers.push({
      id: `${type}-header-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      index: i,
      type
    });
  }
  return headers;
}

/**
 * Migrate legacy headers to new header/category architecture
 */
export function migrateLegacyHeaders(legacyHeaders: LegacyGridHeader[], type: 'row' | 'column', gridSize: number): {
  headers: GridHeader[];
  categories: GridCategory[];
} {
  // Generate headers for all rows/columns (infrastructure)
  const headers = generateHeaders(type, gridSize);
  
  // Convert legacy headers to categories
  const categories: GridCategory[] = legacyHeaders.map(legacy => ({
    id: legacy.id,
    title: legacy.title,
    startIndex: legacy.startIndex,
    endIndex: legacy.endIndex,
    type,
    style: legacy.style
  }));
  
  return { headers, categories };
}

/**
 * Ensure headers exist for all rows/columns (auto-generate missing ones)
 */
export function ensureHeadersExist(layout: GridLayout): GridLayout {
  const updatedLayout = { ...layout };
  
  // Ensure row headers exist
  if (layout.showRowHeaders) {
    if (!updatedLayout.rowHeaders || updatedLayout.rowHeaders.length !== layout.rows) {
      updatedLayout.rowHeaders = generateHeaders('row', layout.rows);
    }
  }
  
  // Ensure column headers exist
  if (layout.showColumnHeaders) {
    if (!updatedLayout.columnHeaders || updatedLayout.columnHeaders.length !== layout.columns) {
      updatedLayout.columnHeaders = generateHeaders('column', layout.columns);
    }
  }
  
  return updatedLayout;
}

/**
 * Get visible categories (handles overlapping/conflicting categories)
 */
export function getVisibleCategories(categories: GridCategory[], gridSize: number): GridCategory[] {
  if (!categories || categories.length === 0) return [];
  
  // First, filter out categories that reference indices beyond the current grid dimensions
  const validCategories = categories.filter(category => 
    category.startIndex >= 1 && 
    category.startIndex <= gridSize && 
    category.endIndex >= 1 && 
    category.endIndex <= gridSize &&
    category.startIndex <= category.endIndex
  );
  
  // Sort categories by span size (descending) so larger categories take precedence
  const sortedCategories = [...validCategories].sort((a, b) => {
    const spanA = a.endIndex - a.startIndex + 1;
    const spanB = b.endIndex - b.startIndex + 1;
    return spanB - spanA; // Larger spans first
  });
  
  const visibleCategories = [];
  const processedIndices = new Set<number>();

  // Process categories in order of span size (largest first)
  for (const category of sortedCategories) {
    // Check if any index in this category's range is already processed
    let alreadyProcessed = false;
    for (let i = category.startIndex; i <= category.endIndex; i++) {
      if (processedIndices.has(i)) {
        alreadyProcessed = true;
        break;
      }
    }
    
    // If no indices are processed, this category is visible
    if (!alreadyProcessed) {
      visibleCategories.push(category);
      // Mark all indices covered by this category as processed
      for (let i = category.startIndex; i <= category.endIndex; i++) {
        processedIndices.add(i);
      }
    }
  }
  
  return visibleCategories;
}

/**
 * Adjusts categories when a row is inserted at a specific position
 */
export function adjustCategoriesForRowInsertion(
  categories: GridCategory[],
  insertionRow: number
): GridCategory[] {
  return categories.map(category => {
    // Categories at or after insertion point: shift both indices down
    if (category.startIndex >= insertionRow) {
      return {
        ...category,
        startIndex: category.startIndex + 1,
        endIndex: category.endIndex + 1
      };
    }

    // Categories spanning insertion point: increase span
    if (category.startIndex < insertionRow && category.endIndex >= insertionRow) {
      return {
        ...category,
        endIndex: category.endIndex + 1
      };
    }

    // Categories entirely above insertion point: no change
    return category;
  });
}

/**
 * Adjusts categories when a column is inserted at a specific position
 */
export function adjustCategoriesForColumnInsertion(
  categories: GridCategory[],
  insertionColumn: number
): GridCategory[] {
  return categories.map(category => {
    // Categories at or after insertion point: shift both indices right
    if (category.startIndex >= insertionColumn) {
      return {
        ...category,
        startIndex: category.startIndex + 1,
        endIndex: category.endIndex + 1
      };
    }

    // Categories spanning insertion point: increase span
    if (category.startIndex < insertionColumn && category.endIndex >= insertionColumn) {
      return {
        ...category,
        endIndex: category.endIndex + 1
      };
    }

    // Categories entirely to the left of insertion point: no change
    return category;
  });
}

/**
 * Adjusts categories when a row is removed
 */
export function adjustCategoriesForRowRemoval(
  categories: GridCategory[],
  removedRow: number
): GridCategory[] {
  return categories
    .map(category => {
      // Categories that span across the removed row: decrease end index
      if (category.startIndex < removedRow && category.endIndex >= removedRow) {
        return {
          ...category,
          endIndex: category.endIndex - 1
        };
      }

      // Categories entirely below the removed row: shift both indices up
      if (category.startIndex > removedRow) {
        return {
          ...category,
          startIndex: category.startIndex - 1,
          endIndex: category.endIndex - 1
        };
      }

      // Categories entirely above the removed row: no change
      return category;
    })
    .filter(category => category.startIndex <= category.endIndex); // Remove categories that become invalid
}

/**
 * Adjusts categories when a column is removed
 */
export function adjustCategoriesForColumnRemoval(
  categories: GridCategory[],
  removedColumn: number
): GridCategory[] {
  return categories
    .map(category => {
      // Categories that span across the removed column: decrease end index
      if (category.startIndex < removedColumn && category.endIndex >= removedColumn) {
        return {
          ...category,
          endIndex: category.endIndex - 1
        };
      }

      // Categories entirely to the right of the removed column: shift both indices left
      if (category.startIndex > removedColumn) {
        return {
          ...category,
          startIndex: category.startIndex - 1,
          endIndex: category.endIndex - 1
        };
      }

      // Categories entirely to the left of the removed column: no change
      return category;
    })
    .filter(category => category.startIndex <= category.endIndex); // Remove categories that become invalid
}
